import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  BarChart3, Users, IndianRupee, FolderKanban, AlertTriangle, Clock, 
  CheckCircle, RefreshCw, ChevronLeft, ChevronRight, Bell, CalendarCheck
} from 'lucide-react';
import { toast } from 'sonner';
import TeamAttendanceReport from '../components/TeamAttendanceReport';

const ReportsPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('projects');
  const [projectReport, setProjectReport] = useState(null);
  const [teamReport, setTeamReport] = useState(null);
  const [financialReport, setFinancialReport] = useState(null);
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teamMonth, setTeamMonth] = useState(new Date().getMonth() + 1);
  const [teamYear, setTeamYear] = useState(new Date().getFullYear());

  useEffect(() => { fetchReports(); }, []);
  useEffect(() => { if (activeTab === 'team') fetchTeamReport(); }, [teamMonth, teamYear]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const [proj, team, fin, dig] = await Promise.all([
        api.get('/reports/projects'),
        api.get(`/reports/team?month=${teamMonth}&year=${teamYear}`),
        api.get('/reports/financial'),
        api.get('/reports/digest').catch(() => ({ data: null })),
      ]);
      setProjectReport(proj.data);
      setTeamReport(team.data);
      setFinancialReport(fin.data);
      setDigest(dig.data);
    } catch (err) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamReport = async () => {
    try {
      const res = await api.get(`/reports/team?month=${teamMonth}&year=${teamYear}`);
      setTeamReport(res.data);
    } catch (err) {
      console.error('fetchTeamReport failed:', err);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]"></div></div>;
  }

  const tabs = [
    { key: 'projects', label: 'Projects', icon: FolderKanban },
    { key: 'team', label: 'Team', icon: Users },
    { key: 'attendance', label: 'Team Attendance', icon: CalendarCheck },
    { key: 'financial', label: 'Financial', icon: IndianRupee },
    { key: 'digest', label: 'Daily Digest', icon: Bell },
  ];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">Reports</h1>
          <p className="text-zinc-500 text-sm mt-1">Analytics & insights</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchReports} className="border-[#F5A623]/30 text-[#F5A623] hover:bg-[#F5A623]/10">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-white/10">
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.key ? 'text-[#F5A623] border-[#F5A623]' : 'text-zinc-500 border-transparent hover:text-white'
            }`} data-testid={`tab-${tab.key}`}>
            <tab.icon className="h-4 w-4" /> {tab.label}
          </button>
        ))}
      </div>

      {/* Project Reports */}
      {activeTab === 'projects' && projectReport && (
        <div className="space-y-4" data-testid="project-reports">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Total Projects" value={projectReport.total_projects} />
            <StatCard label="Completed" value={projectReport.completed} color="text-emerald-400" />
            <StatCard label="Completion Rate" value={`${projectReport.completion_rate}%`} color="text-[#F5A623]" />
            <StatCard label="Avg Delivery" value={`${projectReport.avg_delivery_days}d`} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-3">By Brand</h3>
                {Object.entries(projectReport.by_brand).map(([brand, count]) => (
                  <div key={brand} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                    <span className={`text-sm ${brand === 'triobliss' ? 'text-pink-400' : 'text-[#F5A623]'}`}>
                      {brand === 'triobliss' ? 'Trio Bliss' : 'Wittyshadows'}
                    </span>
                    <span className="text-white font-medium">{count}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-3">By Status</h3>
                {Object.entries(projectReport.by_status).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                    <span className="text-sm text-zinc-400 capitalize">{status.replace(/_/g, ' ')}</span>
                    <span className="text-white font-medium">{count}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
          {projectReport.overdue.length > 0 && (
            <Card className="bg-[#121212] border-rose-500/20">
              <CardContent className="p-4">
                <h3 className="text-rose-400 font-medium mb-3 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Overdue Projects</h3>
                <div className="space-y-2">
                  {projectReport.overdue.map(p => (
                    <div key={p.id} className="flex items-center justify-between p-2 bg-rose-500/5 rounded border border-rose-500/10">
                      <div>
                        <p className="text-white text-sm">{p.title}</p>
                        <p className="text-xs text-zinc-500">{p.client_name}</p>
                      </div>
                      <Badge variant="outline" className="bg-rose-500/10 text-rose-400 border-rose-500/20">{p.days_overdue}d overdue</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Team Reports */}
      {activeTab === 'team' && teamReport && (
        <div className="space-y-4" data-testid="team-reports">
          <div className="flex items-center gap-2">
            <button onClick={() => { if (teamMonth === 1) { setTeamMonth(12); setTeamYear(teamYear - 1); } else setTeamMonth(teamMonth - 1); }} className="p-1 text-zinc-400 hover:text-white"><ChevronLeft className="h-5 w-5" /></button>
            <span className="text-white font-medium">{new Date(teamYear, teamMonth - 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</span>
            <button onClick={() => { if (teamMonth === 12) { setTeamMonth(1); setTeamYear(teamYear + 1); } else setTeamMonth(teamMonth + 1); }} className="p-1 text-zinc-400 hover:text-white"><ChevronRight className="h-5 w-5" /></button>
          </div>
          <div className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-xs text-zinc-400 font-medium px-4 py-3">Member</th>
                  <th className="text-center text-xs text-zinc-400 font-medium px-3 py-3">Tasks Done</th>
                  <th className="text-center text-xs text-zinc-400 font-medium px-3 py-3">Hours</th>
                  <th className="text-center text-xs text-zinc-400 font-medium px-3 py-3">Present</th>
                  <th className="text-center text-xs text-zinc-400 font-medium px-3 py-3">Shoots</th>
                  <th className="text-center text-xs text-zinc-400 font-medium px-3 py-3">Leaves</th>
                  <th className="text-center text-xs text-zinc-400 font-medium px-3 py-3">Late</th>
                </tr>
              </thead>
              <tbody>
                {teamReport.members.map(m => (
                  <tr key={m.user_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <p className="text-white text-sm font-medium">{m.name}</p>
                      <p className="text-[10px] text-zinc-500 capitalize">{(m.roles || []).join(', ').replace(/_/g, ' ')}</p>
                    </td>
                    <td className="text-center text-sm text-white px-3">{m.tasks_completed}/{m.tasks_total}</td>
                    <td className="text-center text-sm text-white px-3">{m.hours_worked}h</td>
                    <td className="text-center text-sm text-emerald-400 px-3">{m.present_days}</td>
                    <td className="text-center text-sm text-blue-400 px-3">{m.shoot_days}</td>
                    <td className="text-center text-sm text-rose-400 px-3">{m.leave_days}</td>
                    <td className="text-center px-3">
                      {m.late_count > 0 && <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-400 border-amber-500/20">{m.late_count} late</Badge>}
                      {m.half_day_count > 0 && <Badge variant="outline" className="text-[10px] bg-rose-500/10 text-rose-400 border-rose-500/20 ml-1">{m.half_day_count} half</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Team Attendance Report */}
      {activeTab === 'attendance' && (
        <div data-testid="team-attendance-reports">
          <TeamAttendanceReport />
        </div>
      )}

      {/* Financial Reports */}
      {activeTab === 'financial' && financialReport && (
        <div className="space-y-4" data-testid="financial-reports">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Total Revenue" value={`₹${(financialReport.total_revenue || 0).toLocaleString('en-IN')}`} color="text-white" />
            <StatCard label="Collected" value={`₹${(financialReport.total_collected || 0).toLocaleString('en-IN')}`} color="text-emerald-400" />
            <StatCard label="Outstanding" value={`₹${(financialReport.outstanding || 0).toLocaleString('en-IN')}`} color="text-amber-400" />
            <StatCard label="Collection Rate" value={`${financialReport.collection_rate}%`} color="text-[#F5A623]" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-3">Revenue by Brand</h3>
                {Object.entries(financialReport.by_brand).map(([brand, val]) => (
                  <div key={brand} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                    <span className={`text-sm ${brand === 'triobliss' ? 'text-pink-400' : 'text-[#F5A623]'}`}>
                      {brand === 'triobliss' ? 'Trio Bliss' : 'Wittyshadows'}
                    </span>
                    <span className="text-white font-medium">₹{val.toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-3">Monthly Trend</h3>
                {(financialReport.monthly || []).map(m => (
                  <div key={m.month} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
                    <span className="text-xs text-zinc-400">{m.month}</span>
                    <div className="flex gap-3 text-xs">
                      <span className="text-white">₹{(m.booked_value || 0).toLocaleString('en-IN')}</span>
                      <span className="text-emerald-400">₹{(m.collected || 0).toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Daily Digest */}
      {activeTab === 'digest' && digest && (
        <div className="space-y-4" data-testid="daily-digest">
          <div className="flex items-center justify-between">
            <p className="text-zinc-400 text-sm">Summary for {digest.date}</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={async () => {
                try { await api.post('/notifications/send-deadline-reminders'); toast.success('Deadline reminders sent!'); } catch { toast.error('Failed'); }
              }} className="border-rose-500/30 text-rose-400 hover:bg-rose-500/10 text-xs" data-testid="send-reminders-btn">
                <AlertTriangle className="h-3 w-3 mr-1" /> Send Deadline Reminders
              </Button>
              <Button size="sm" onClick={async () => {
                try { await api.post('/notifications/send-daily-digest'); toast.success('Digest emailed to managers!'); } catch { toast.error('Failed'); }
              }} className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black text-xs" data-testid="send-digest-btn">
                <Bell className="h-3 w-3 mr-1" /> Email Digest to Managers
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-3 flex items-center gap-2"><Users className="h-4 w-4 text-[#F5A623]" /> Attendance</h3>
                <p className="text-sm text-zinc-400 mb-2">{digest.attendance.total_checked_in} checked in</p>
                {digest.attendance.late.length > 0 && (
                  <div className="mb-2">
                    <p className="text-xs text-amber-400 mb-1">Late Arrivals:</p>
                    {digest.attendance.late.map((l, i) => (
                      <p key={i} className="text-xs text-zinc-300">{l.name} ({l.status === 'half_day' ? 'Half Day' : 'Late'})</p>
                    ))}
                  </div>
                )}
                {digest.attendance.absent.length > 0 && (
                  <div>
                    <p className="text-xs text-rose-400 mb-1">Absent:</p>
                    <p className="text-xs text-zinc-400">{digest.attendance.absent.join(', ')}</p>
                  </div>
                )}
              </CardContent>
            </Card>
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-3 flex items-center gap-2"><CheckCircle className="h-4 w-4 text-emerald-400" /> Tasks Completed</h3>
                {digest.tasks_completed.length === 0 ? (
                  <p className="text-sm text-zinc-500">No tasks completed yesterday</p>
                ) : (
                  digest.tasks_completed.map((t, i) => (
                    <div key={i} className="py-1.5 border-b border-white/5 last:border-0">
                      <p className="text-xs text-white">{t.title}</p>
                      <p className="text-[10px] text-zinc-500">{t.assigned_to} — {t.hours}h</p>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-3 flex items-center gap-2"><Clock className="h-4 w-4 text-amber-400" /> Upcoming Deadlines</h3>
                {digest.upcoming_deadlines.length === 0 ? (
                  <p className="text-sm text-zinc-500">No deadlines this week</p>
                ) : (
                  digest.upcoming_deadlines.map((d, i) => (
                    <div key={i} className="py-1.5 border-b border-white/5 last:border-0">
                      <p className="text-xs text-white">{d.title}</p>
                      <p className="text-[10px] text-zinc-500">{new Date(d.deadline).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</p>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, color = 'text-white' }) => (
  <Card className="bg-[#121212] border-white/10">
    <CardContent className="p-4">
      <p className="text-xs text-zinc-500 uppercase tracking-wider">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
    </CardContent>
  </Card>
);

export default ReportsPage;
